import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAuth } from '../contexts/AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import ProductSearch from '../components/ProductSearch';
import { setHasMore, setProducts } from '../store/slices/productsSlice';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  ShoppingBagIcon,
  TagIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const ProductsPageNew = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, logout, getToken } = useAuth();
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [productsStatus, setProductsStatus] = useState({});

  const {
    items: allProducts,
    searchResults,
    hasMore,
    currentPage,
    searchTerm,
    totalCount
  } = useSelector(state => state.products);

  const fetchProducts = useCallback(async (pageNumber) => {
    if (isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const token = getToken();
      if (!token) throw new Error('No token found');

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/basalam/products`, {
        params: { page: pageNumber, per_page: 20 },
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const { products: newProducts, pagination } = response.data;

      if (!newProducts || !pagination) {
        dispatch(setHasMore(false));
        return;
      }

      const filteredProducts = newProducts.filter(product => {
        const isAvailable = product.status?.id === 2976 || product.status?.title === 'در دسترس';
        const hasStock = (product.inventory || product.stock || 0) > 0;
        return isAvailable && hasStock;
      });

      dispatch(setProducts(filteredProducts));
      dispatch(setHasMore(pageNumber < pagination.total_pages));
    } catch (error) {
      console.error('خطا در دریافت محصولات:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      } else {
        dispatch(setHasMore(false));
      }
    } finally {
      setIsLoadingMore(false);
      setIsInitialFetch(false);
    }
  }, [isLoadingMore, getToken, logout, navigate, dispatch]);

  useEffect(() => {
    if (isAuthenticated && isInitialFetch) {
      fetchProducts(1);
      fetchProductsStatus();
    } else if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, fetchProducts, navigate, isInitialFetch]);

  const fetchProductsStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/products/bulk-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProductsStatus(response.data || {});
    } catch (error) {
      console.error('Error fetching products status:', error);
    }
  };

  const formatPrice = useCallback((price) => {
    return Math.round(price / 10).toLocaleString('fa-IR').replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
  }, []);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      if (!searchTerm) {
        setIsLoadingMore(true);
        fetchProducts(currentPage + 1);
      }
    }
  }, [isLoadingMore, hasMore, searchTerm, currentPage, fetchProducts]);

  const ProductCard = React.memo(({ product, formatPrice, status }) => {
    const hasRule = status?.hasRule || false;
    const hasCompetitors = status?.hasCompetitors || false;
    const priceStatus = status?.priceStatus || 'ok';

    const getImageUrl = (product) => {
      if (product.photo?.md) return product.photo.md;
      if (product.photo?.sm) return product.photo.sm;
      if (product.photo?.medium) return product.photo.medium;
      if (product.photo?.small) return product.photo.small;
      if (product.photo?.extra_small) return product.photo.extra_small;
      return '/placeholder.png';
    };

    const getStatusBadge = () => {
      if (priceStatus === 'danger') {
        return (
          <Badge variant="destructive" className="absolute top-2 right-2 shadow-lg">
            <span className="text-xs">⚠️ هشدار</span>
          </Badge>
        );
      }
      if (hasRule && priceStatus !== 'danger') {
        return (
          <Badge className="absolute top-2 right-2 bg-green-500 hover:bg-green-600">
            <span className="text-xs">✅ مناسب</span>
          </Badge>
        );
      }
      if (!hasRule && hasCompetitors) {
        return (
          <Badge variant="secondary" className="absolute top-2 right-2">
            <span className="text-xs">👥 رقیب</span>
          </Badge>
        );
      }
      return null;
    };

    return (
      <Card
        className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
        onClick={() => navigate(`/competitors/${product.id}`)}
      >
        {/* Image */}
        <div className="relative aspect-square overflow-hidden bg-muted">
          <img
            src={getImageUrl(product)}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder.png';
            }}
          />
          {getStatusBadge()}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <h3 className="font-bold text-base line-clamp-2 min-h-[3rem] group-hover:text-primary transition-colors">
            {product.title}
          </h3>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-1">
              <TagIcon className="h-4 w-4 text-primary" />
              <span className="text-lg font-bold text-primary">
                {formatPrice(product.price)}
              </span>
              <span className="text-sm text-muted-foreground">تومان</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <CubeIcon className="h-4 w-4" />
              <span>موجودی: {product.inventory || 0}</span>
            </div>
          </div>

          <Button
            className="w-full mt-2"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/competitors/${product.id}`);
            }}
          >
            <ShoppingBagIcon className="h-4 w-4 ml-2" />
            مدیریت رقبا
          </Button>
        </div>
      </Card>
    );
  });

  const LoadingGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {[...Array(10)].map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <Skeleton className="aspect-square w-full" />
          <div className="p-4 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-full" />
          </div>
        </Card>
      ))}
    </div>
  );

  if (isInitialFetch) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-64" />
        </div>
        <LoadingGrid />
      </div>
    );
  }

  const displayProducts = searchTerm ? searchResults : allProducts;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">محصولات</h1>
          <p className="text-muted-foreground mt-1">
            {displayProducts.length} محصول
            {searchTerm && ` (جستجو: ${searchTerm})`}
          </p>
        </div>
      </div>

      {/* Search */}
      <ProductSearch />

      {/* Products Grid with Infinite Scroll */}
      <InfiniteScroll
        dataLength={displayProducts.length}
        next={loadMore}
        hasMore={hasMore && !searchTerm}
        loader={
          <div className="col-span-full flex justify-center py-8">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-muted-foreground">در حال بارگذاری...</span>
            </div>
          </div>
        }
        endMessage={
          displayProducts.length > 0 && (
            <p className="text-center mt-8 text-muted-foreground py-4">
              تمام محصولات نمایش داده شده‌اند
            </p>
          )
        }
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
      >
        {displayProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            formatPrice={formatPrice}
            status={productsStatus[product.id]}
          />
        ))}
      </InfiniteScroll>

      {/* Empty State */}
      {displayProducts.length === 0 && !isLoadingMore && (
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-muted p-6">
              <ShoppingBagIcon className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">هیچ محصولی یافت نشد</h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm
                  ? 'جستجوی دیگری امتحان کنید'
                  : 'محصولی برای نمایش وجود ندارد'}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProductsPageNew;
